import React, { useState, useRef, useEffect } from 'react';
import { dataService } from '../../services/dataService';
import { usePipeline } from './PipelineContext';
import { confirmDialog } from '../../utils/dialog';
import {
  FIELD_TYPES, STATUS_COLORS, typeIcon,
  FieldRow, RecordCell, RecordCtxMenu, LinkPicker,
} from '../Collections/parts';

// ── Main modal ───────────────────────────────────────────────────────────────
export default function CollectionModal({ nodeId, allNodes, onUpdate, onClose, opsMode, onSendToAgents }) {
  const node = allNodes.find(n => n.id === nodeId);
  const allCollections = allNodes.filter(n => n.type === 'collectionNode' && n.id !== nodeId);
  const { workspaceId } = usePipeline();

  const [tab, setTab] = useState('fields');
  const [label, setLabel] = useState(() => node?.data.label || '');
  const [fields, setFields] = useState(() => node?.data.fields || []);
  const [records, setRecords] = useState([]);
  const [recordsByCol, setRecordsByCol] = useState({}); // relation targets' records
  const [flashIds, setFlashIds] = useState({}); // record id -> true, briefly, when its values change

  // Snapshot of each record's values from the previous render, so we can detect
  // server-side changes (e.g. an automation flipping Status) and flash the row.
  const prevValsRef = useRef(null);

  useEffect(() => {
    if (!workspaceId || !nodeId) return;
    const unsub = dataService.subscribeRecords(workspaceId, nodeId, setRecords);
    return () => unsub && unsub();
  }, [workspaceId, nodeId]);

  // Records arrive live via onSnapshot (including changes an automation makes a
  // second or two after you save). The update can be easy to miss, so flash any
  // row whose values changed since the last snapshot.
  useEffect(() => {
    const next = {};
    for (const r of records) next[r.id] = JSON.stringify(r.values || {});
    const prev = prevValsRef.current;
    if (prev) {
      const changed = {};
      for (const id of Object.keys(next)) {
        if (prev[id] !== undefined && prev[id] !== next[id]) changed[id] = true;
      }
      if (Object.keys(changed).length) {
        setFlashIds(changed);
        const t = setTimeout(() => setFlashIds({}), 1400);
        prevValsRef.current = next;
        return () => clearTimeout(t);
      }
    }
    prevValsRef.current = next;
  }, [records]);

  // Subscribe to records of other collections so relation dropdowns and the
  // link picker can resolve their target records from the subcollection.
  const otherColIdsKey = allCollections.map(c => c.id).sort().join(',');
  useEffect(() => {
    if (!workspaceId) return;
    const colIds = otherColIdsKey ? otherColIdsKey.split(',') : [];
    const unsubs = colIds.map(colId =>
      dataService.subscribeRecords(workspaceId, colId, (recs) => {
        setRecordsByCol(prev => ({ ...prev, [colId]: recs }));
      })
    );
    return () => unsubs.forEach(u => u && u());
  }, [workspaceId, otherColIdsKey]);

  // New field form
  const [nfName, setNfName] = useState('');
  const [nfType, setNfType] = useState('text');

  // Staged new-record draft (committed on explicit Save)
  const [newRec, setNewRec] = useState({});
  const [addingRec, setAddingRec] = useState(false);

  // Record interactions
  const [recordCtx, setRecordCtx]   = useState(null); // { record, x, y }
  const [linkPicker, setLinkPicker] = useState(null); // { record, field }


  const setFieldsAndSave = (next) => { setFields(next); onUpdate(nodeId, { fields: next, label }); };
  const setLabelAndSave = (v) => { setLabel(v); onUpdate(nodeId, { label: v, fields }); };

  // Field ops
  const addField = () => {
    if (!nfName.trim()) return;
    const f = {
      id: `f${Date.now()}`,
      name: nfName.trim(),
      type: nfType,
      ...(nfType === 'select' ? { options: [] } : {}),
    };
    setNfName(''); setNfType('text');
    setFieldsAndSave([...fields, f]);
  };

  const updateField = (fId, updates) =>
    setFieldsAndSave(fields.map(f => f.id === fId ? { ...f, ...updates } : f));

  const deleteField = (fId) =>
    setFieldsAndSave(fields.filter(f => f.id !== fId));

  // Record ops — write directly to Firestore subcollection.
  // New records are STAGED in a draft row and only written on an explicit Save,
  // so we never create a blank record (a blank create would fire the
  // record_added trigger on empty data before the user fills anything in).
  const startNewRecord = () => { setNewRec({}); setAddingRec(true); };
  const cancelNewRecord = () => { setNewRec({}); setAddingRec(false); };
  const commitNewRecord = () => {
    const hasValue = Object.values(newRec).some(
      v => v !== undefined && v !== null && String(v).trim() !== ''
    );
    if (!hasValue) return; // require at least one value — never write a blank record
    dataService.addRecord(workspaceId, nodeId, newRec);
    setNewRec({});
    setAddingRec(false);
  };

  const updateRecord = (rId, fId, val) => {
    dataService.patchRecord(workspaceId, nodeId, rId, { [fId]: val });
  };

  const deleteRecord = async (rId) => {
    if (!(await confirmDialog({ title: 'Delete record', message: 'Delete this record? This cannot be undone.', confirmText: 'Delete', danger: true }))) return;
    dataService.deleteRecord(workspaceId, nodeId, rId);
  };

  if (!node) return null;

  const termCollection = opsMode ? 'Records' : 'Collection';

  return (
    <div className="cm-backdrop" onClick={onClose}>
      <div className="cm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="cm-hdr">
          <span className="cm-hdr-icon">📁</span>
          <input
            className="cm-hdr-title"
            value={label}
            onChange={e => setLabel(e.target.value)}
            onBlur={e => setLabelAndSave(e.target.value)}
            placeholder={`${termCollection} name`}
          />
          <div className="cm-hdr-meta">
            <span className="cm-hdr-badge">{fields.length} field{fields.length !== 1 ? 's' : ''}</span>
            <span className="cm-hdr-badge">{records.length} record{records.length !== 1 ? 's' : ''}</span>
          </div>
          <button className="cm-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="cm-tabs">
          <button className={`cm-tab ${tab === 'fields' ? 'active' : ''}`} onClick={() => setTab('fields')}>
            ⚙️ Fields
          </button>
          <button data-testid="cm-tab-records" className={`cm-tab ${tab === 'records' ? 'active' : ''}`} onClick={() => setTab('records')}>
            📄 Records
          </button>
        </div>

        {/* Body */}
        <div className="cm-body">

          {/* ── FIELDS TAB ── */}
          {tab === 'fields' && (
            <div className="cm-fields-panel">
              <div className="cm-fields-intro">
                Define the structure of your {termCollection.toLowerCase()}. Each field becomes a column in your records.
                Use <strong>Select</strong> for status/priority dropdowns, and <strong>Relation</strong> to link records to another collection.
              </div>

              {fields.length === 0 && (
                <div className="cm-empty-state">
                  <div className="cm-empty-icon">🗂️</div>
                  <div className="cm-empty-text">No fields yet — add your first field below</div>
                </div>
              )}

              <div className="cm-fields-list">
                {fields.map((f, i) => (
                  <FieldRow
                    key={f.id}
                    field={f}
                    allCollections={allCollections}
                    onUpdate={(updates) => updateField(f.id, updates)}
                    onDelete={() => deleteField(f.id)}
                    isFirst={i === 0}
                  />
                ))}
              </div>

              <div className="cm-add-field-row">
                <input
                  className="cm-add-field-name"
                  placeholder="New field name…"
                  value={nfName}
                  onChange={e => setNfName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addField()}
                />
                <select className="cm-add-field-type" value={nfType} onChange={e => setNfType(e.target.value)}>
                  {FIELD_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                  ))}
                </select>
                <button className="cm-add-field-btn" onClick={addField} disabled={!nfName.trim()}>
                  + Add field
                </button>
              </div>
            </div>
          )}

          {/* ── RECORDS TAB ── */}
          {tab === 'records' && records.length > 0 && (
            <div className="cm-records-hint">
              💡 Right-click any row to link it to another collection or delete it.
              {onSendToAgents && ' Use → Send to agent to hand a record to the connected agent team to process in chat.'}
            </div>
          )}
          {tab === 'records' && (
            <div className="cm-records-panel">
              {fields.length === 0 ? (
                <div className="cm-empty-state">
                  <div className="cm-empty-icon">🗂️</div>
                  <div className="cm-empty-text">
                    Switch to the <strong>Fields</strong> tab to define your schema first, then come back to add records.
                  </div>
                </div>
              ) : (
                <>
                  <div className="cm-table-wrap">
                    <table className="cm-table">
                      <thead>
                        <tr>
                          <th className="cm-th cm-th-num">#</th>
                          {fields.map(f => (
                            <th key={f.id} className="cm-th">
                              <span className="cm-th-icon">{typeIcon(f.type)}</span>
                              {f.name}
                              {f.type === 'relation' && allCollections.find(c => c.id === f.targetCollectionId) && (
                                <span className="cm-th-rel-tag">
                                  → {allCollections.find(c => c.id === f.targetCollectionId)?.data.label}
                                </span>
                              )}
                            </th>
                          ))}
                          {onSendToAgents && <th className="cm-th cm-th-trigger"></th>}
                          <th className="cm-th cm-th-del"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.length === 0 && !addingRec && (
                          <tr>
                            <td colSpan={fields.length + 2} className="cm-td-empty">
                              No records yet — click "+ Add record" below
                            </td>
                          </tr>
                        )}
                        {records.map((r, idx) => {
                          const statusField = fields.find(f => f.name.toLowerCase() === 'status' && f.type === 'select');
                          const statusVal   = statusField ? r.values?.[statusField.id] : null;
                          const statusColor = statusVal ? STATUS_COLORS[statusVal] : null;
                          return (
                            <tr
                              key={r.id}
                              className={`cm-tr ${statusColor ? 'has-status' : ''}${flashIds[r.id] ? ' cm-tr-flash' : ''}`}
                              style={statusColor ? { borderLeft: `3px solid ${statusColor}` } : {}}
                              onContextMenu={e => { e.preventDefault(); setRecordCtx({ record: r, x: e.clientX, y: e.clientY }); }}
                              title="Right-click to link or delete"
                            >
                              <td className="cm-td cm-td-num">{idx + 1}</td>
                              {fields.map(f => (
                                <td key={f.id} className="cm-td">
                                  <RecordCell
                                    field={f}
                                    value={r.values?.[f.id]}
                                    allCollections={allCollections}
                                    recordsByCol={recordsByCol}
                                    onChange={val => updateRecord(r.id, f.id, val)}
                                  />
                                </td>
                              ))}
                              {onSendToAgents && (
                                <td className="cm-td cm-td-trigger">
                                  <button
                                    className="cm-rec-trigger-btn"
                                    title="Hand this record to the connected agent team to process in chat"
                                    onClick={() => { onSendToAgents(nodeId, r.id); onClose(); }}
                                  >→ Send to agent</button>
                                </td>
                              )}
                              <td className="cm-td cm-td-del">
                                <button className="cm-rec-del-btn" onClick={() => deleteRecord(r.id)} title="Delete record">🗑️</button>
                              </td>
                            </tr>
                          );
                        })}
                        {/* Staged draft row — values only persist on Save */}
                        {addingRec && (
                          <tr data-testid="cm-draft-row" className="cm-tr cm-tr-new">
                            <td className="cm-td cm-td-num">＋</td>
                            {fields.map(f => (
                              <td key={f.id} className="cm-td">
                                <RecordCell
                                  field={f}
                                  value={newRec[f.id]}
                                  allCollections={allCollections}
                                  recordsByCol={recordsByCol}
                                  onChange={val => setNewRec(p => ({ ...p, [f.id]: val }))}
                                />
                              </td>
                            ))}
                            {onSendToAgents && <td className="cm-td cm-td-trigger"></td>}
                            <td className="cm-td cm-td-del">
                              <button data-testid="cm-save-record" className="cm-rec-save-btn" onClick={commitNewRecord} title="Save record" aria-label="Save record">✓</button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="cm-records-actions">
                    {addingRec ? (
                      <span className="cm-add-rec-hint">Fill the new row, then click the green ✓ to save it. <button className="cm-add-rec-cancel" onClick={cancelNewRecord}>Cancel</button></span>
                    ) : (
                      <button data-testid="cm-add-record" className="cm-add-rec-btn" onClick={startNewRecord}>+ Add record</button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Record right-click menu */}
        {recordCtx && (
          <RecordCtxMenu
            x={recordCtx.x}
            y={recordCtx.y}
            record={recordCtx.record}
            fields={fields}
            allCollections={allCollections}
            onLink={(field) => { setLinkPicker({ record: recordCtx.record, field }); setRecordCtx(null); }}
            onDelete={deleteRecord}
            onClose={() => setRecordCtx(null)}
          />
        )}

        {/* Link picker */}
        {linkPicker && (
          <LinkPicker
            field={linkPicker.field}
            allCollections={allCollections}
            recordsByCol={recordsByCol}
            currentValue={linkPicker.record.values?.[linkPicker.field.id]}
            onPick={(val) => updateRecord(linkPicker.record.id, linkPicker.field.id, val)}
            onClose={() => setLinkPicker(null)}
          />
        )}
      </div>
    </div>
  );
}
